import { Text } from '@mantine/core'
import { IconCircleDashed, IconHelpCircle } from '@tabler/icons-react'
import { StatusBadge } from '@lighthouse/design'
import { NOT_MEASURED } from '../../utils/format'
import { verdictLabel, verdictRole } from './scheduleFormat'
import type { ScheduleLoadVerdict } from '../../types/scheduleLoad'

/**
 * One verdict, as a badge.
 *
 * The four judged verdicts wear the status scale through `StatusBadge`, which
 * always pairs the colour with an icon and a word. The two that mean "this page
 * cannot say" wear neutral ink and a glyph that makes the same admission:
 *
 *  - **Indeterminate** — a question mark. Not a warning triangle: the known
 *    demand is a floor, and a warning would still be a judgement about the load
 *    that the numbers do not support (doc 11 §6.3). Build plan #2's lesson — an
 *    icon is a claim and can contradict the colour beside it — applies here to
 *    the glyph as much as to the tone.
 *  - **Unmeasured** — a dashed circle, the same dashed edge the timeline gives
 *    the cell: there is no capacity measurement to judge against.
 *
 * Null (a withheld case verdict when capacity is unmeasured) is a dash, never a
 * badge.
 */
export function VerdictBadge({ verdict }: { verdict: ScheduleLoadVerdict | null }) {
  if (verdict == null) return <Text span size="sm" c="dimmed">{NOT_MEASURED}</Text>

  const role = verdictRole(verdict)
  if (role) return <StatusBadge status={role} label={verdictLabel(verdict)} />

  const dashed = verdict === 'Unmeasured'
  return (
    <span
      className="lh-badge"
      style={{
        color: 'var(--color-text-secondary)',
        borderColor: 'var(--color-text-disabled)',
        borderStyle: dashed ? 'dashed' : 'solid',
      }}
    >
      {dashed ? <IconCircleDashed size={12} stroke={2} /> : <IconHelpCircle size={12} stroke={2} />}
      {verdictLabel(verdict)}
    </span>
  )
}
